import * as React from 'react';
import { Menu, Dropdown, Space, Modal, message, Spin, Select,MenuProps } from 'antd';
import {
  AppstoreOutlined,
  UserOutlined,
  LogoutOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { CurrentUser } from 'app/shared/model/CurrentUser';
import './Header.css';
import { useMsal } from '@azure/msal-react';
import * as api from 'app/shared/api/getDivision';
import { Link, useLocation } from 'react-router-dom';
import { useSignalREventError } from 'app/helpers/signalRHelper';
import {
  getSelectedDivisionIdRaw,
  notifySelectedDivisionChanged,
  removeSelectedDivisionId,
  setSelectedDivisionId,
} from 'app/shared/division/selectedDivisionStorage';
import { HELP_SUPPORT_URL, HELP_USER_MANUAL_URL } from './helpLinks';

const useDivision = () => {
  const [divisions, setDivisions] = React.useState<{ id: string; name: string }[]>([]);
  const [selectedDivision, setSelectedDivisionState] = React.useState<string | undefined>();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const isInitialDivisionSyncRef = React.useRef(true);
  const setSelectedDivision = (id: string | undefined) => {
    setSelectedDivisionState(id);
    if (id) {
      setSelectedDivisionId(id);
    } else {
      removeSelectedDivisionId();
    }
  };

  React.useEffect(() => {
    setLoading(true);
    api.getActiveDivisions()
      .then((d) => {
        const mappedDivisions = d.map(div => ({
          id: String(div.id),
          name: div.name
        }));
        setDivisions(mappedDivisions);

        const stored = getSelectedDivisionIdRaw();
        const valid = mappedDivisions.find(div => div.id === stored);
        const nextId = valid ? valid.id : mappedDivisions[0]?.id;
        if (nextId) {
          isInitialDivisionSyncRef.current = true;
          setSelectedDivision(nextId);
          // Pricelist may mount before divisions finish loading; sync without treating as user change.
          notifySelectedDivisionChanged();
          window.setTimeout(() => {
            isInitialDivisionSyncRef.current = false;
          }, 0);
        }
      })
      .catch(() => setError('Failed to load divisions'))
      .finally(() => setLoading(false));
  }, []);


  return {
    divisions,
    selectedDivision,
    setSelectedDivision,
    loading,
    error,
    isInitialDivisionSyncRef,
  };
};

interface HeaderProps {
  formLoaded: boolean;
  user: CurrentUser;
  version: string;
  isProcessRunning?: boolean;
  resetAllFilters?: () => void;
}

const Header: React.FC<HeaderProps> = ({ user, version, isProcessRunning = false, resetAllFilters}) => {
    const { instance } = useMsal();
    const {
      divisions,
      selectedDivision,
      setSelectedDivision,
      loading: loadingDivisions,
      error: divisionError,
      isInitialDivisionSyncRef,
    } = useDivision();
    const [pendingDivision, setPendingDivision] = React.useState<string | undefined>();
    const location = useLocation();
    const shouldHideDivisionDropdown = React.useMemo(() => {
      const path = location.pathname.toLowerCase();
      return (
        path.startsWith('/discount') ||
        path.startsWith('/ports-and-terminals') ||
        path.startsWith('/course-intensity-manager') ||
        path.startsWith('/course-language-manager') ||
        path.startsWith('/product-category-manager') ||
        path.startsWith('/account-category-manager') ||
        path.startsWith('/division-manager') ||
        path.startsWith('/centre-manager') ||
        path.startsWith('/school-manager') ||
        path.startsWith('/pricelist/holiday-manager') ||
        path.startsWith('/calculator') ||
        path.startsWith('/tax-manager')
      );
    }, [location.pathname]);
    const selectedKeys = React.useMemo(() => getSelectedMenuKeys(location.pathname), [location.pathname]);

    const handleLogout = () => {
        instance.logoutRedirect();
    };

        const userMenuItems: MenuProps['items'] = [
      {
        key: 'version',
        label: `Products and Pricing ${import.meta.env.VITE_CURRENT_ENV}`,
        disabled: true,
        style: { fontWeight: 'bold' },
      },
      {
        type: 'divider',
      },
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: <a onClick={handleLogout}>Sign out</a>,
      },
    ];

    const userMenu: MenuProps = {
      items: userMenuItems,
    };

    const helpMenuItems: MenuProps['items'] = [
      {
        key: 'user-manual',
        label: (
          <a href={HELP_USER_MANUAL_URL} target="_blank" rel="noopener noreferrer">
            Open User Manual Guide
          </a>
        ),
      },
      {
        key: 'contact-support',
        label: <a href={HELP_SUPPORT_URL}>Contact Support</a>,
      },
    ];

    const helpMenu: MenuProps = {
      items: helpMenuItems,
    };

    const onDivisionChange = (divisionId: string) => {
      if (isInitialDivisionSyncRef.current) return;
      if (divisionId === selectedDivision) return;

      if (isProcessRunning) {
        message.error(
          'There are unfinished processes. Please wait till the current process finishes to switch division.'
        );
        return;
      }

    setPendingDivision(divisionId);

    Modal.confirm({
      title: 'Confirm Division Change',
      content:
        'Are you sure you want to change the division? Any unsaved changes will be lost.',
      okText: 'Yes, Switch Division',
      cancelText: 'Cancel',
      onOk: () => {
        setSelectedDivision(divisionId);
        if (typeof resetAllFilters === 'function') resetAllFilters();
        message.success('Division changed successfully');
        notifySelectedDivisionChanged();
        setPendingDivision(undefined);
      },
      onCancel: () => {
        setPendingDivision(undefined);
      },
    });
  };

  const navMenuItems = React.useMemo(() => ([
            {
              key: 'logo',
              icon: <AppstoreOutlined />,
              label: 'Products and Pricing',
            },
            {
              key: 'pricelist',
              label: <Link to="/pricelist">Pricelist</Link>,
            },
            {
              key: 'products',
              label: <Link to="/products">Products</Link>,
            },
            {
              key: 'discounts',
              label: 'Discounts',
              popupOffset: [0, -8],
              children: [
                { key: 'discounts-list', label: <Link to="/">Promotions</Link> },
                { key: 'early-bird-offer', label: <Link to="/early-bird-offer">Early Bird Offer</Link> },
              ],
            },
            {
              key: 'agents',
              label: <Link to="/agents">Commissions</Link>,
            },
            {
              key: 'cancellations',
              label: <Link to="/cancellations">Cancellations</Link>,
            },
            {
              key: 'pricing-reference-data',
              label: 'Pricing Reference Data',
              popupClassName: 'header-first-level-submenu',
              popupOffset: [0, -8],
              children: [
                {
                  key: 'ports-and-terminals',
                  label: <Link to="/ports-and-terminals">Ports and Terminals Manager</Link>,
                },
                {
                  key: 'course-settings',
                  label: 'Course Settings',
                  children: [
                    {
                      key: 'course-intensity-manager',
                      label: <Link to="/course-intensity-manager">Course Intensity Manager</Link>,
                    },
                    {
                      key: 'course-language-manager',
                      label: <Link to="/course-language-manager">Course Language Manager</Link>,
                    },
                  ],
                },
                {
                  key: 'commission-category-manager',
                  label: <Link to="/commission-category-manager">Commission Category Manager</Link>,
                },
                {
                  key: 'tax-manager',
                  label: <Link to="/tax-manager">Tax Manager</Link>,
                },
                {
                  key: 'product-category-manager',
                  label: <Link to="/product-category-manager">Product Category Manager</Link>,
                },
                {
                  key: 'account-category-manager',
                  label: <Link to="/account-category-manager">Account Category Manager</Link>,
                },
                 {
                  key: 'division-manager',
                  label: <Link to="/division-manager">Division Manager</Link>,
                },
                  {
                  key: 'centre-manager',
                  label: <Link to="/centre-manager">Centre Manager</Link>,
                },
                {
                  key: 'school-manager',
                  label: <Link to="/school-manager">School Manager</Link>,
                },
                {
                  key: 'region-manager',
                  label: <Link to="/region-manager">Region Manager</Link>,
                },
                {
                  key: 'holiday-manager',
                  label: <Link to="/pricelist/holiday-manager">Holiday Manager</Link>,
                },
              ],
            },
            {
              key: 'new-pricing-year',
              label: <Link to="/new-pricing-year">New Pricing Year</Link>,
            },
            {
              key: 'calculator',
              label: <Link to="/calculator">Calculator</Link>,
            },
  ]), []);

  return (
    <div className="header-wrapper">
      {useSignalREventError()}
      <Menu
        mode="horizontal"
        theme="dark"
        className="header-menu"
        items={navMenuItems}
        selectedKeys={selectedKeys}
      />

      <div className="header-right" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <Dropdown
          menu={helpMenu}
          placement="bottom"
          trigger={['hover', 'click']}
          arrow
        >
          <a
            className="ant-dropdown-link header-help-link"
            href="#"
            aria-label="Help"
            onClick={(e) => e.preventDefault()}
          >
            <QuestionCircleOutlined />
          </a>
        </Dropdown>
        <div>
          {!shouldHideDivisionDropdown && (
            loadingDivisions ? (
              <Spin size="small" />
            ) : divisionError ? (
              <span style={{ color: 'red', fontSize: 12 }}>{divisionError}</span>
            ) : (
              <Select
                id="division-select"
                style={{ width: 180 }}
                value={pendingDivision || selectedDivision}
                onChange={onDivisionChange}
                options={divisions.map((div) => ({
                  label: div.name,
                  value: div.id,
                }))}
                placeholder="Select Division"
                disabled={loadingDivisions}
                showSearch={{ optionFilterProp: 'label' }}
              />
            )
          )}
        </div>
          <Dropdown menu={userMenu} placement="bottomRight" trigger={['click']}>
            <a className="ant-dropdown-link" onClick={(e) => e.preventDefault()}>
              <Space>
                <UserOutlined />
                {user.fullName}
              </Space>
            </a>
          </Dropdown>
        </div>
      </div>
    );
  };

  function getSelectedMenuKeys(pathname: string): string[] {
    const path = pathname.toLowerCase();
    if (path.startsWith('/early-bird-offer')) {
      return ['early-bird-offer'];
    }
    if (path === '/' || path.startsWith('/discount')) {
      return ['discounts-list'];
    }
    if (path.startsWith('/pricelist/holiday-manager')) {
      return ['holiday-manager'];
    }
    if (path == '/pricelist' || path.startsWith('/pricelist?')) {
      return ['pricelist'];
    }
    if (path.startsWith('/pricelist')) {
      return ['pricelist'];
    }
    if (path.startsWith('/products')) {
      return ['products'];
    }
    if (path.startsWith('/cancellations')) {
      return ['cancellations'];
    }
    if (path.startsWith('/agents')) {
      return ['agents'];
    }
    if (path.startsWith('/ports-and-terminals')) {
      return ['ports-and-terminals'];
    }
    if (path.startsWith('/course-intensity-manager')) {
      return ['course-intensity-manager'];
    }
    if (path.startsWith('/course-language-manager')) {
      return ['course-language-manager'];
    }
    if (path.startsWith('/commission-category-manager')) {
      return ['commission-category-manager'];
    }
    if (path.startsWith('/tax-manager')) {
      return ['tax-manager'];
    }
    if (path.startsWith('/product-category-manager')) {
      return ['product-category-manager'];
    }
    if (path.startsWith('/account-category-manager')) {
      return ['account-category-manager'];
    }
    if (path.startsWith('/division-manager')) {
      return ['division-manager'];
    }
    if (path.startsWith('/centre-manager')) {
      return ['centre-manager'];
    }
    if (path.startsWith('/new-pricing-year')) {
      return ['new-pricing-year'];
    }
    if (path.startsWith('/school-manager')) {
      return ['school-manager'];
    }
    if (path.startsWith('/region-manager')) {
      return ['region-manager'];
    }
    if (path.startsWith('/calculator')) {
      return ['calculator'];
    }
    return [];
}

export default Header;